import {useEffect, useRef} from 'react'
import {SidebarMenuMain} from './SidebarMenuMain'
import './SidebarMenu.css'

// Metronic's ScrollComponent used to keep the menu's scroll position in a cookie; the menu now
// scrolls by CSS (SidebarMenu.css), so the position is kept here, per browser tab.
export const SIDEBAR_MENU_SCROLL_KEY = 'asa.sidebarMenu.scrollTop'

const readSavedScrollTop = (): number => {
  try {
    return Number(window.sessionStorage.getItem(SIDEBAR_MENU_SCROLL_KEY)) || 0
  } catch {
    // Storage is blocked (private window, site data off): the menu opens at the top.
    return 0
  }
}

const saveScrollTop = (scrollTop: number) => {
  try {
    window.sessionStorage.setItem(SIDEBAR_MENU_SCROLL_KEY, String(Math.round(scrollTop)))
  } catch {
    // Storage is blocked: the position is simply not remembered.
  }
}

const SidebarMenu = () => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const restorePendingRef = useRef(true)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const menu = menuRef.current
    const savedScrollTop = readSavedScrollTop()
    if (!wrapper || !menu || savedScrollTop <= 0 || typeof ResizeObserver === 'undefined') {
      restorePendingRef.current = false
      return
    }

    // On a page load the menu mounts still hidden or short, and the browser would clamp the
    // position to 0; apply it once the menu is tall enough to hold it.
    const observer = new ResizeObserver(() => {
      if (!restorePendingRef.current) {
        observer.disconnect()
      } else if (wrapper.scrollHeight - wrapper.clientHeight >= savedScrollTop) {
        restorePendingRef.current = false
        observer.disconnect()
        wrapper.scrollTop = savedScrollTop
      }
    })
    observer.observe(wrapper)
    observer.observe(menu)
    return () => observer.disconnect()
  }, [])

  return (
    <div className='app-sidebar-menu overflow-hidden flex-column-fluid'>
      <div
        ref={wrapperRef}
        id='kt_app_sidebar_menu_wrapper'
        className='app-sidebar-wrapper hover-scroll-overlay-y my-5'
        onScroll={(event) => {
          // A scroll before the saved position was applied is the user's: theirs wins.
          restorePendingRef.current = false
          saveScrollTop(event.currentTarget.scrollTop)
        }}
      >
        <div
          ref={menuRef}
          className='menu menu-column menu-rounded menu-sub-indention px-3'
          id='#kt_app_sidebar_menu'
          data-kt-menu='true'
          data-kt-menu-expand='false'
        >
          <SidebarMenuMain />
        </div>
      </div>
    </div>
  )
}

export {SidebarMenu}
